import { Data, Route } from '@angular/router';

/**
 * Extras type for use in route creator functions
 */
export type RouteExtras = Omit<
  Partial<Route>,
  | 'path'
  | 'component'
  | 'loadComponent'
  | 'data'
  | 'redirectTo'
  | 'loadChildren'
  | 'canMatch'
>;

/**
 * @interface BaseTypedRouteConfig
 * @description
 * Common configuration shared by all typed routes.
 *
 * @template TData - The shape of the strongly typed `data` object.
 */
interface BaseTypedRouteConfig<TData extends Data> {
  /** Path for the route (e.g., 'users/:id') */
  path: string;

  /** Strongly typed route data */
  data: TData;

  /**
   * Optional route properties (e.g., `canActivate`, `resolve`, `title`, etc.).
   * Excludes keys irrelevant to typed component routes:
   * - path, component, loadComponent, data, redirectTo, loadChildren, canMatch
   */
  extras?: RouteExtras;
}

/**
 * @interface ComponentTypedRouteConfig
 * @description
 * Eagerly loaded component route config.
 */
interface ComponentTypedRouteConfig<TData extends Data>
  extends BaseTypedRouteConfig<TData> {
  /** Eagerly loaded component */
  component: Route['component'];
  loadComponent?: never;
}

/**
 * @interface LazyTypedRouteConfig
 * @description
 * Lazily loaded component route config.
 */
interface LazyTypedRouteConfig<TData extends Data>
  extends BaseTypedRouteConfig<TData> {
  /** Lazily loaded component */
  loadComponent: Route['loadComponent'];
  component?: never;
}

/**
 * @typedef TypedRouteConfig
 * @description
 * Union type for a typed route: either eager or lazy component (exactly one).
 */
export type TypedRouteConfig<TData extends Data> =
  | ComponentTypedRouteConfig<TData>
  | LazyTypedRouteConfig<TData>;

/**
 * @function createTypedRoute
 * @description
 * Creates a strongly typed Angular `Route` with a `data` object.
 * Enforces that exactly one of `component` or `loadComponent` is provided.
 *
 * @template TData - Type of the strongly typed route data.
 *
 * @usage
 * ```ts
 * // ✅ Eager route
 * const dashboardRoute = createTypedRoute({
 *   path: 'dashboard',
 *   component: DashboardComponent,
 *   data: { title: 'Dashboard', requiresAuth: true },
 * });
 *
 * // ✅ Lazy route
 * const editRoute = createTypedRoute({
 *   path: 'edit/:id',
 *   loadComponent: () =>
 *     import('@app/path/edit/edit.component').then(m => m.EditComponent),
 *   data: { title: 'Edit Item', requiresAuth: true },
 *   extras: { canActivate: [AuthGuard] },
 * });
 *
 * // ❌ Error: must specify exactly one of component/loadComponent
 * createTypedRoute({
 *   path: 'broken',
 *   data: { title: 'Oops' }
 * });
 * ```
 */
export function createTypedRoute<TData extends Data>(
    config: TypedRouteConfig<TData>
): Route & { data: TData } {
  const { path, data, extras = {} } = config;
  const route: Route & { data: TData } = { path, data, ...extras };

  // Type narrowing ensures the property is defined
  if ('component' in config) {
    route.component = config.component!;
  } else if ('loadComponent' in config) {
    route.loadComponent = config.loadComponent!;
  }

  return route;
}
